/**
 * Задачи: создание и подбор родителя.
 *
 * Иерархия в трекере ровно на три уровня, и её держит бэкенд: эпик наверху,
 * под ним история, задача или баг, под ними подзадача. Здесь она повторена
 * одной картой — не чтобы проверять вместо сервера, а чтобы не предлагать
 * человеку родителя, на котором форма всё равно получит отказ.
 */
import { issueApi } from '@/api'
import { cdnUrl } from '@/api/cdn'
import { CLOSED_STATUS_CATEGORIES } from '@/api/statusCategory'

import { emptyFilters, orNothing, type IssueFilters } from './filters'

import type {
  IssueServiceListIssueGroupsFilterStatusCategoriesEnum,
  IssueServiceListIssuesFilterStatusCategoriesEnum,
  V1Issue,
  V1IssueType,
  V1Priority,
} from '@/api/clients/tracker-issue'

/** Кто чьим родителем бывает; типа нет в ключах — значит родителя не имеет */
const PARENT_TYPES: Partial<Record<V1IssueType, V1IssueType[]>> = {
  ISSUE_TYPE_STORY: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_TASK: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_BUG: ['ISSUE_TYPE_EPIC'],
  ISSUE_TYPE_SUBTASK: ['ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG'],
}

/** Приоритеты сверху вниз — тем же порядком их и выбирают в форме и в отборе */
export const PRIORITIES: V1Priority[] = [
  'PRIORITY_HIGHEST',
  'PRIORITY_HIGH',
  'PRIORITY_MEDIUM',
  'PRIORITY_LOW',
  'PRIORITY_LOWEST',
]

/** Какие типы задач есть в проекте — по его профилю */
export const typesOfProfile = (profile: string): V1IssueType[] =>
  profile === 'TYPE_PROFILE_SIMPLE'
    ? ['ISSUE_TYPE_TASK', 'ISSUE_TYPE_SUBTASK']
    : ['ISSUE_TYPE_EPIC', 'ISSUE_TYPE_STORY', 'ISSUE_TYPE_TASK', 'ISSUE_TYPE_BUG', 'ISSUE_TYPE_SUBTASK']

export const parentTypesOf = (type: V1IssueType | ''): V1IssueType[] =>
  (type && PARENT_TYPES[type]) || []

/**
 * Кого заводят под задачей этого типа. Обратная сторона той же карты: у эпика
 * детьми бывают история, задача и баг — по кнопке заводим задачу, это привычный
 * случай; у истории, задачи и бага — подзадача; у подзадачи детей не бывает
 * вовсе, и пустая строка здесь означает именно это (DEV-1593).
 */
const CHILD_TYPE: Partial<Record<V1IssueType, V1IssueType>> = {
  ISSUE_TYPE_EPIC: 'ISSUE_TYPE_TASK',
  ISSUE_TYPE_STORY: 'ISSUE_TYPE_SUBTASK',
  ISSUE_TYPE_TASK: 'ISSUE_TYPE_SUBTASK',
  ISSUE_TYPE_BUG: 'ISSUE_TYPE_SUBTASK',
}

export const childTypeOf = (type: V1IssueType | ''): V1IssueType | '' =>
  (type && CHILD_TYPE[type]) || ''

export interface CreateIssuePayload {
  projectId: string
  title: string
  descriptionMd: string
  type: V1IssueType
  priority: V1Priority
  /** Колонка, в которой задача появится; пусто — первая открытая на доске */
  columnId?: string
  parentId?: string
  assigneeId?: string
  /** Срок в виде YYYY-MM-DD, как его отдаёт календарь дизайн-системы */
  dueDate?: string
  /** Кого редактор описания заметил в упоминаниях */
  mentionedUserIds?: string[]
}

export interface CreatedIssue {
  id: string
  key: string
  columnId: string
}

/**
 * Пустые поля не отправляем вовсе: `column_id`, `parent_id` и `assignee_id`
 * объявлены `optional string` с проверкой на uuid, и пустая строка её не
 * проходит — сервер ответит 400 вместо «поле не заполнено».
 */
export const createIssue = async (payload: CreateIssuePayload): Promise<CreatedIssue> => {
  const { data } = await issueApi.issueServiceCreateIssue({
    projectId: payload.projectId,
    title: payload.title,
    descriptionMd: payload.descriptionMd,
    type: payload.type,
    priority: payload.priority,
    ...(payload.columnId ? { columnId: payload.columnId } : {}),
    ...(payload.parentId ? { parentId: payload.parentId } : {}),
    ...(payload.assigneeId ? { assigneeId: payload.assigneeId } : {}),
    // Срок — день, а сервер ждёт момент времени: берём начало дня в поясе
    // человека, иначе «сегодня» в Ханое уезжает на вчера.
    ...(payload.dueDate ? { dueDate: new Date(`${payload.dueDate}T00:00:00`).toISOString() } : {}),
    ...(payload.mentionedUserIds?.length ? { mentionedUserIds: payload.mentionedUserIds } : {}),
  })

  return {
    id: data.issue?.id ?? '',
    key: data.issue?.key ?? '',
    columnId: data.issue?.columnId ?? '',
  }
}

/** Строка списка задач */
export interface IssueRow {
  id: string
  key: string
  title: string
  type: V1IssueType | ''
  priority: V1Priority | ''
  assigneeId: string
  assigneeName: string
  /** Ссылка на фото исполнителя; пусто — в строке кружок с инициалами */
  assigneeAvatar: string
  /** Момент времени от сервера; в таблице от него остаётся день */
  dueDate: string
  /** Колонка доски — она же статус задачи в списке */
  columnId: string
  /** Родитель в иерархии: по нему собирается дерево эпика */
  parentId: string
  /** Подзадачи: сколько всего и сколько закрыто */
  subtasks: { total: number; done: number }
  /** Когда задачу правили в последний раз — колонка дефолтной сортировки (DEV-2041) */
  updatedAt: string
  /** Когда закрыли; пусто у открытой. На табе «Закрытые» показывается вместо updatedAt */
  completedAt: string
}

/** Экспортирован ради подзадач: они те же строки, только с фильтром по родителю */
export const toRow = (issue: V1Issue): IssueRow => ({
  id: issue.id ?? '',
  key: issue.key ?? '',
  title: issue.title ?? '',
  type: issue.type ?? '',
  priority: issue.priority ?? '',
  assigneeId: issue.assigneeId ?? '',
  assigneeName: issue.assigneeName ?? '',
  assigneeAvatar: cdnUrl(issue.assigneeAvatarUrl),
  dueDate: issue.dueDate ?? '',
  columnId: issue.columnId ?? '',
  parentId: issue.parentId ?? '',
  subtasks: { total: Number(issue.childCount ?? 0), done: Number(issue.childDoneCount ?? 0) },
  updatedAt: issue.updatedAt ?? '',
  completedAt: issue.completedAt ?? '',
})

/** По чему список умеет сортировать — ровно то, что принимает сервер */
export type IssueSortField =
  | 'key'
  | 'priority'
  | 'due_date'
  | 'created_at'
  | 'updated_at'
  | 'completed_at'

export interface IssueSort {
  field: IssueSortField
  desc: boolean
}

export interface IssuePage {
  issues: IssueRow[]
  /** Всего задач под отбором — из них таблица и считает страницы */
  total: number
}

/** Таб «Показ»: открытые по умолчанию — закрытые копились в общем списке */
export type ShowTab = 'open' | 'done' | 'all'

/**
 * Сужение по табу для запроса. «Закрытые» — это категория колонки, а не имя:
 * колонки переименовывают, категорию — нет.
 */
const tabQuery = (tab: ShowTab): { onlyOpen?: boolean; statusCategories?: string[] } => {
  switch (tab) {
    case 'open':
      return { onlyOpen: true }
    // Вкладка закрытых показывает обе терминальные категории: отклонённую
    // задачу иначе не найти нигде, кроме доски — в «открытых» её нет по
    // определению, а фильтра «только отклонённые» пользователь не просил.
    case 'done':
      return { statusCategories: [...CLOSED_STATUS_CATEGORIES] }
    default:
      return {}
  }
}

/**
 * Условие догрузки одной группы секционного списка (DEV-2043): та же ось, по
 * которой сервер резал группы. Пустой ключ — NULL-группа, она адресуется
 * IS NULL-флагами, появившимися ради неё (DEV-2031).
 */
export interface GroupSlice {
  by: 'status' | 'assignee' | 'priority' | 'type'
  key: string
}

export const listIssues = async (
  projectId: string,
  filters: IssueFilters = emptyFilters(),
  sort: IssueSort = { field: 'updated_at', desc: true },
  page = 1,
  perPage = 25,
  tab: ShowTab = 'all',
  slice?: GroupSlice,
): Promise<IssuePage> => {
  const narrowing = tabQuery(tab)
  const columnIds = slice?.by === 'status' && slice.key ? [slice.key] : orNothing(filters.columnIds)

  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    slice?.by === 'assignee' && slice.key ? [slice.key] : orNothing(filters.assigneeIds),
    undefined,
    // Ключи групп сервер отдаёт сырыми значениями базы (TASK, HIGH), а фильтр
    // принимает enum'ы protobuf — достраиваем префикс.
    slice?.by === 'type' ? [`ISSUE_TYPE_${slice.key}` as V1IssueType] : orNothing(filters.types),
    slice?.by === 'priority'
      ? [`PRIORITY_${slice.key}` as V1Priority]
      : orNothing(filters.priorities),
    columnIds,
    filters.search.trim() || undefined,
    undefined,
    narrowing.onlyOpen,
    undefined,
    undefined, // filterProjectIds — проект задан первым аргументом
    narrowing.statusCategories as
      | IssueServiceListIssuesFilterStatusCategoriesEnum[]
      | undefined,
    undefined, // filterDueAfter
    undefined, // filterAncestorId — им берут поддерево эпика, см. listSubtree
    orNothing(filters.reporterIds),
    slice?.by === 'assignee' && !slice.key ? true : undefined, // filterUnassigned
    undefined, // filterNoDueDate
    slice?.by === 'status' && !slice.key ? true : undefined, // filterNoColumn
    sort.field,
    sort.desc,
    String(page),
    String(perPage),
  )

  return {
    issues: (data.issues ?? []).map(toRow),
    // Счётчики приходят строками: в protobuf это int64, а он не влезает в number.
    total: Number(data.pageInfo?.total ?? 0),
  }
}

/**
 * Сквозной поиск задач (DEV-2049, DEV-2050) — SearchIssues как есть: ключ и
 * голый номер сервер понимает сам (совпадений по номеру может быть несколько,
 * текущий проект первым — передай projectId).
 */
export const searchIssuesPage = async (
  query: string,
  page = 1,
  perPage = 25,
  projectId?: string,
): Promise<IssuePage> => {
  const { data } = await issueApi.issueServiceSearchIssues(
    query,
    projectId,
    String(page),
    String(perPage),
  )

  return {
    issues: (data.issues ?? []).map(toRow),
    total: Number(data.pageInfo?.total ?? 0),
  }
}

/** Пачечное изменение (DEV-2054): что панель меняет разом */
export interface BulkPatch {
  columnId?: string
  close?: boolean
  assigneeId?: string
  priority?: V1Priority
}

/**
 * Массовое действие одним вызовом (DEV-2036): раньше панель слала запрос на
 * задачу и упиралась в лимитер; уведомление получателю теперь одно на пачку.
 */
export const bulkUpdateIssues = async (
  issueIds: string[],
  patch: BulkPatch,
): Promise<{ wipExceeded: boolean }> => {
  const { data } = await issueApi.issueServiceBulkUpdateIssues({ issueIds, ...patch })

  return { wipExceeded: Boolean(data.columnWip?.exceeded) }
}

/** Секция серверного группового листинга (DEV-2034) */
export interface ServerGroup {
  key: string
  /** Имя колонки или исполнителя с сервера; пусто — подпись знает экран */
  title: string
  /** Честный размер группы под отбором, а не длина issues */
  total: number
  issues: IssueRow[]
}

export interface GroupPage {
  groups: ServerGroup[]
  /** Открытые и закрытые всего отбора — без сужения по табу: для футера «закрытых N →» */
  openTotal: number
  doneTotal: number
}

/** Оси группировки в терминах сервера */
const GROUP_BY_ENUM = {
  status: 'ISSUE_GROUP_BY_COLUMN',
  assignee: 'ISSUE_GROUP_BY_ASSIGNEE',
  priority: 'ISSUE_GROUP_BY_PRIORITY',
  type: 'ISSUE_GROUP_BY_TYPE',
} as const

/**
 * Группы со счётчиками и первыми строками — одним запросом (DEV-2034).
 * Клиентская группировка страницы рвала группы и врала в счётчиках; сервер
 * считает по всей выборке тем же WHERE, что и строки.
 */
export const listIssueGroups = async (
  projectId: string,
  filters: IssueFilters,
  sort: IssueSort,
  groupBy: keyof typeof GROUP_BY_ENUM,
  tab: ShowTab,
  perGroup = 10,
): Promise<GroupPage> => {
  const narrowing = tabQuery(tab)

  const { data } = await issueApi.issueServiceListIssueGroups(
    projectId,
    orNothing(filters.assigneeIds),
    undefined, // filterLabelIds
    orNothing(filters.types),
    orNothing(filters.priorities),
    orNothing(filters.columnIds),
    filters.search.trim() || undefined,
    undefined, // filterParentId
    narrowing.onlyOpen,
    undefined, // filterDueBefore
    undefined, // filterProjectIds
    narrowing.statusCategories as
      | IssueServiceListIssueGroupsFilterStatusCategoriesEnum[]
      | undefined,
    undefined, // filterDueAfter
    undefined, // filterAncestorId
    orNothing(filters.reporterIds),
    undefined, // filterUnassigned
    undefined, // filterNoDueDate
    undefined, // filterNoColumn
    GROUP_BY_ENUM[groupBy],
    sort.field,
    sort.desc,
    perGroup,
  )

  return {
    groups: (data.groups ?? []).map((group) => ({
      key: group.key ?? '',
      title: group.title ?? '',
      total: Number(group.total ?? 0),
      issues: (group.issues ?? []).map(toRow),
    })),
    openTotal: Number(data.openTotal ?? 0),
    doneTotal: Number(data.doneTotal ?? 0),
  }
}

/**
 * Перенос задачи. `beforeIssueId` — задача, над которой она встаёт; пусто
 * означает конец колонки, и это не то же самое, что «куда-нибудь»: без него
 * карточка после переноса уезжала бы вниз независимо от того, куда её положили.
 *
 * Конец колонки — это отсутствие поля, а не пустая строка: поле объявлено
 * `optional string` с проверкой на uuid, и пустая строка её не проходит —
 * сервер отвечает 400 на каждый перенос в конец.
 */
export const moveIssue = async (
  issueId: string,
  columnId: string,
  beforeIssueId?: string,
): Promise<void> => {
  await issueApi.issueServiceMoveIssue(
    issueId,
    beforeIssueId ? { columnId, beforeIssueId } : { columnId },
  )
}

/**
 * Смена исполнителя из списка. Снять исполнителя — это пустая строка: поле
 * `optional string` без проверки на uuid, и сервер понимает её как «никто».
 */
export const setAssignee = async (issueId: string, assigneeId: string): Promise<void> => {
  await issueApi.issueServiceUpdateIssue(issueId, { assigneeId })
}

/** Смена приоритета из строки — контекст-меню и инлайн-пикер (DEV-2052) */
export const setPriority = async (issueId: string, priority: V1Priority): Promise<void> => {
  await issueApi.issueServiceUpdateIssue(issueId, { priority })
}

/** Срок из строки; пустая строка снимает срок */
export const setDueDate = async (issueId: string, dueDate: string): Promise<void> => {
  await issueApi.issueServiceUpdateIssue(issueId, { dueDate })
}

/**
 * «Закрыть» из строки (DEV-2052) = BulkUpdateIssues(close, 1 id): первую
 * done-колонку выбирает сервер — клиент не обязан знать колонки проекта, и
 * закрытие работает и с /my, где колонки чужих проектов не загружены.
 * Возвращает колонку, куда задача легла, — для оптимистики и undo.
 */
export const closeIssue = async (issueId: string): Promise<{ columnId: string }> => {
  const { data } = await issueApi.issueServiceBulkUpdateIssues({
    issueIds: [issueId],
    close: true,
  })

  return { columnId: data.issues?.[0]?.columnId ?? '' }
}

export interface ParentCandidate {
  id: string
  key: string
  title: string
  type: V1IssueType | ''
}

/** Сколько кандидатов в родители подтягиваем: дальше человеку проще искать */
const PARENT_LIMIT = 100

/**
 * Кандидаты в родители — задачи подходящего типа в том же проекте. Список
 * приходит целиком и ищется на месте: он короткий, а запрос на каждую букву
 * ради сотни строк не окупается.
 */
export const listParentCandidates = async (
  projectId: string,
  types: V1IssueType[],
): Promise<ParentCandidate[]> => {
  if (!types.length) return []

  const { data } = await issueApi.issueServiceListIssues(
    projectId,
    undefined,
    undefined,
    types,
    undefined,
    undefined,
    undefined,
    undefined,
    // Закрытые эпики в родители не предлагаем: заводить в них новую задачу —
    // это почти всегда промах.
    true,
    undefined,
    undefined, // filterProjectIds
    undefined, // filterStatusCategories
    undefined, // filterDueAfter
    undefined, // filterAncestorId
    undefined, // filterReporterIds
    undefined, // filterUnassigned
    undefined, // filterNoDueDate
    undefined, // filterNoColumn
    'key',
    false,
    '1',
    String(PARENT_LIMIT),
  )

  return (data.issues ?? []).map((issue) => ({
    id: issue.id ?? '',
    key: issue.key ?? '',
    title: issue.title ?? '',
    type: issue.type ?? '',
  }))
}
